import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, Repeat, Euro, Wallet, Clock, Briefcase, CheckCircle2, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Page, PageTitle, Loading, ErrorBox, Empty } from "@/components/site/Common";
import { JobRow } from "@/components/site/JobRow";
import { useMe } from "@/hooks/useMe";
import { get } from "@/lib/api";
import { eur, fdate, tradeName } from "@/lib/hz";

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={testid}>
    <Icon className="size-5 text-orange-600" />
    <div className="mt-3 font-heading text-2xl font-extrabold text-slate-900">{value}</div>
    <div className="text-sm text-slate-500">{label}</div>
  </div>
);

const JobList = ({ q, role, empty }) =>
  q.isLoading ? <Loading /> : q.isError ? <ErrorBox /> : !q.data.length ? <Empty>{empty}</Empty> : (
    <div className="space-y-3">{q.data.map((j) => <JobRow key={j.id} job={j} role={role} />)}</div>
  );

const ACTIVE = ["abierto", "solicitado", "asignado", "en_curso", "completado"];

function ClientPanel({ me }) {
  const jobs = useQuery({ queryKey: ["jobs", "mine"], queryFn: () => get("/jobs/mine") });
  const rec = useQuery({ queryKey: ["recurrences"], queryFn: () => get("/recurrences/mine") });
  const list = jobs.data ?? [];
  const quotes = list.filter((j) => j.status === "abierto").reduce((a, j) => a + j.quotes_count, 0);
  return (
    <Page>
      <PageTitle eyebrow="Mi panel" title={`Hola, ${me.name.split(" ")[0]}`} sub="Tus solicitudes, en directo desde la app HandyZoom.">
        <Button asChild size="lg" className="rounded-xl"><Link to="/crear-solicitud" data-testid="panel-new-job-btn"><Plus />Nueva solicitud</Link></Button>
      </PageTitle>
      <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
        <Stat icon={Briefcase} label="Trabajos activos" value={list.filter((j) => ACTIVE.includes(j.status)).length} testid="stat-active" />
        <Stat icon={Euro} label="Presupuestos por revisar" value={quotes} testid="stat-quotes" />
        <Stat icon={Wallet} label="Pendiente de pago" value={list.filter((j) => j.status === "facturado").length} testid="stat-unpaid" />
        <Stat icon={CheckCircle2} label="Pagados" value={list.filter((j) => j.status === "pagado").length} testid="stat-paid" />
      </div>
      <h2 className="mt-12 font-heading text-xl font-bold">Mis trabajos</h2>
      <div className="mt-4"><JobList q={jobs} role="cliente" empty="Aún no has publicado ninguna solicitud." /></div>
      {!!rec.data?.length && (
        <>
          <h2 className="mt-12 font-heading text-xl font-bold">Mantenimientos periódicos</h2>
          <div className="mt-4 grid gap-3 md:grid-cols-2" data-testid="recurrences-list">
            {rec.data.map((r) => (
              <div key={r.id} className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-500"><Repeat className="size-4 text-orange-600" />Cada {r.interval_months} meses · {r.status}</div>
                <div className="mt-2 font-heading text-lg font-bold">{r.title}</div>
                <div className="mt-1 text-sm text-slate-500">{tradeName(r.trade)} · {r.pro_name} · {eur(r.price)} · próxima: {fdate(r.next_date)}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </Page>
  );
}

function ProPanel({ me }) {
  const earn = useQuery({ queryKey: ["earnings"], queryFn: () => get("/earnings") });
  const open = useQuery({ queryKey: ["jobs", "open"], queryFn: () => get("/jobs/open") });
  const mine = useQuery({ queryKey: ["jobs", "mine"], queryFn: () => get("/jobs/mine") });
  const e = earn.data;
  return (
    <Page>
      <PageTitle eyebrow="Panel profesional" title={`Hola, ${me.name.split(" ")[0]}`} sub="Trabajos disponibles para tus oficios y tu actividad.">
        <Button asChild variant="outline" size="lg" className="rounded-xl"><Link to="/formacion" data-testid="panel-academy-btn"><GraduationCap />Formación</Link></Button>
      </PageTitle>
      <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
        <Stat icon={Euro} label="Facturado" value={eur(e?.invoiced)} testid="stat-invoiced" />
        <Stat icon={Wallet} label="Cobrado" value={eur(e?.collected)} testid="stat-collected" />
        <Stat icon={Clock} label="Pendiente de cobro" value={eur(e?.pending)} testid="stat-pending" />
        <Stat icon={Briefcase} label="Trabajos activos" value={e?.active_jobs ?? "—"} testid="stat-active-jobs" />
      </div>
      <Tabs defaultValue="open" className="mt-12">
        <TabsList>
          <TabsTrigger value="open" data-testid="tab-open-jobs">Trabajos disponibles ({open.data?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="mine" data-testid="tab-my-jobs">Mis trabajos ({mine.data?.length ?? 0})</TabsTrigger>
        </TabsList>
        <TabsContent value="open" className="mt-4"><JobList q={open} role="profesional" empty="No hay trabajos abiertos de tus oficios ahora mismo." /></TabsContent>
        <TabsContent value="mine" className="mt-4"><JobList q={mine} role="profesional" empty="Aún no tienes trabajos asignados." /></TabsContent>
      </Tabs>
    </Page>
  );
}

export default function Panel() {
  const { data: me } = useMe();
  return me.role === "profesional" ? <ProPanel me={me} /> : <ClientPanel me={me} />;
}
